import { useCallback, useMemo } from 'react'
import { RefreshCw, Webhook } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchWebhooks, rotateWebhook, DEV_STR as S, type WebhookRow } from './devApi'

/**
 * A13.16 — Webhook-секреты. Rotate a webhook's signing secret. Dormant until
 * `/admin/developers/webhooks` ships. Placeholder permission USERS_VIEW.
 */
export function WebhooksScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<WebhookRow>[]>(
    () => [
      { key: 'url', header: S.url, type: 'text' },
      { key: 'event', header: S.event, type: 'text' },
      { key: 'secret_masked', header: S.secret, type: 'text' },
      { key: 'last_delivery', header: S.lastDelivery, type: 'text' },
      { key: 'failures', header: S.failures, type: 'number' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<WebhookRow>[]>(
    () => [
      {
        id: 'rotate',
        label: S.rotate,
        icon: <RefreshCw size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await rotateWebhook(r.id)
            toast(S.rotated, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchWebhooks, [])
  return (
    <>
      <PageHeader title={S.webhooks} screenId="A13.16" />
      <DataTable<WebhookRow>
        screenId="A13.16"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Webhook size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
